import type { AircraftSummary, FlightSegment, Observation } from "../api";
import type { FormState } from "../App";
import type { TZ } from "../lib/time";
import SearchForm from "./SearchForm";
import AircraftList from "./AircraftList";
import FlightList from "./FlightList";
import FlightSummary from "./FlightSummary";
import { ScrollArea } from "./ui/scroll-area";

type SidebarView = "list" | "flights" | "summary";

interface Props {
  // Timezone
  tz: TZ;
  onTzChange: (tz: TZ) => void;

  // Mobile toggle
  isOpen: boolean;
  onClose: () => void;

  // List view
  form: FormState;
  onFormChange: (f: FormState) => void;
  observations: Observation[];
  onSearch: () => void;
  onReset: () => void;
  onAircraftSelect: (hex: string) => void;
  isSearching: boolean;

  // Detail navigation
  view: SidebarView;
  hex: string;
  segments: FlightSegment[];
  summary: AircraftSummary | null;
  segment: FlightSegment | null;
  onSegmentSelect: (seg: FlightSegment) => void;
  onDetailBack: () => void;
  onDetailClose: () => void;
}

export default function Sidebar({
  tz,
  onTzChange,
  isOpen,
  onClose,
  form,
  onFormChange,
  observations,
  onSearch,
  onReset,
  onAircraftSelect,
  isSearching,
  view,
  hex,
  segments,
  summary,
  segment,
  onSegmentSelect,
  onDetailBack,
  onDetailClose,
}: Props) {
  return (
    <div
      className={[
        "flex flex-col w-[360px] shrink-0 h-full bg-card border-r border-border overflow-hidden",
        "fixed inset-y-0 left-0 z-50 transition-transform duration-300",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "md:relative md:translate-x-0 md:z-auto",
      ].join(" ")}
    >
      {view === "list" && (
        <>
          <div className="shrink-0 px-3 pt-3 pb-1 flex items-start justify-between">
            <div>
              <h1 className="text-base font-semibold text-primary leading-tight">ADS-B Observer</h1>
              <p className="text-[0.65rem] text-muted-foreground font-mono mt-0.5">
                build: {__APP_VERSION__}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onTzChange(tz === "utc" ? "local" : "utc")}
                className="text-[0.65rem] font-mono px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground"
                title="Toggle UTC / local time"
              >
                {tz === "utc" ? "UTC" : "Local"}
              </button>
              <button
                className="md:hidden flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground"
                onClick={onClose}
                aria-label="Close sidebar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-3 pb-3 flex flex-col gap-2">
              <SearchForm form={form} onFormChange={onFormChange} onSearch={onSearch} onReset={onReset} tz={tz} isSearching={isSearching} />
              <AircraftList observations={observations} onAircraftSelect={onAircraftSelect} tz={tz} isSearching={isSearching} />
            </div>
          </ScrollArea>
        </>
      )}

      {view === "flights" && (
        <FlightList
          hex={hex}
          segments={segments}
          onSegmentSelect={onSegmentSelect}
          onClose={onDetailClose}
          tz={tz}
        />
      )}

      {view === "summary" && summary && segment && (
        <FlightSummary
          summary={summary}
          segment={segment}
          onBack={onDetailBack}
          onClose={onDetailBack}
          tz={tz}
        />
      )}
    </div>
  );
}
